import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/axios';
import { useLanguage } from '../context/LanguageContext';
import { MdSupportAgent, MdTask, MdInfo, MdBusiness, MdCategory, MdPerson } from 'react-icons/md';
import { FaUsers, FaFileContract, FaChartLine, FaChartPie, FaTrophy, FaUserTie, FaCalendarAlt } from 'react-icons/fa';
import { TbProgressCheck } from 'react-icons/tb';
import { BiTask } from 'react-icons/bi';
import { MdAssignment } from 'react-icons/md';
import { MdCheckCircle } from 'react-icons/md';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    assignedCases: 0,
    totalCases: 0,
    openCases: 0,
    inProgressCases: 0,
    closedCases: 0,
    totalAgents: 0,
    totalCustomers: 0,
    totalContracts: 0
  });
  
  const [topCustomers, setTopCustomers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [personalStats, setPersonalStats] = useState({
    totalAssigned: 0,
    inProgress: 0,
    closed: 0
  });



  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Get user info from localStorage
        const userInfo = JSON.parse(localStorage.getItem('user'));
        const userId = userInfo?.id;
        // Check for both possible field names
        let companyId = userInfo?.company_id || userInfo?.companyId;
        
        
        // If company ID is missing, try to get it from another source or use a default
        if (!companyId) {
          console.warn('Company ID is missing from user info! Using default value 1');
          companyId = 1; // Use a default company ID
        }
        
                 // Fetch support cases
         let assignedCases = 0;
         let allCases = [];
         let openCases = 0;
         let inProgressCases = 0;
         let closedCases = 0;
        
        try {
          // Fetch assigned cases
          
          const assignedResponse = await api.get(
            `/support-cases?owner_id=${userId}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          assignedCases = assignedResponse.data.length;
          
          
          // Fetch all cases
          const allCasesResponse = await api.get(
            '/support-cases',
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          allCases = allCasesResponse.data;
          
          
                     // Count cases by status
           openCases = allCases.filter(c => 
             c.case_status === 'OPEN' || 
             c.case_status === 'Open'
           ).length;
           inProgressCases = allCases.filter(c => 
             c.case_status === 'IN_PROGRESS' || 
             c.case_status === 'IN PROGRESS' || 
             c.case_status === 'In Progress'
           ).length;
           closedCases = allCases.filter(c => 
             c.case_status === 'CLOSED' || 
             c.case_status === 'Closed'
           ).length;
          
          
          // Debug: Check all unique case_status values
          const uniqueStatuses = [...new Set(allCases.map(c => c.case_status))];
          
          // Calculate personal stats (all time, not just monthly)
          const myCases = assignedResponse.data;
          
          // More flexible status filtering
          const myInProgress = myCases.filter(c => 
            c.case_status === 'IN_PROGRESS' || 
            c.case_status === 'IN PROGRESS' || 
            c.case_status === 'In Progress'
          ).length;
          const myClosed = myCases.filter(c => 
            c.case_status === 'CLOSED' || 
            c.case_status === 'Closed'
          ).length;
          
          
          // Debug: Check unique case_status values for my cases
          const myUniqueStatuses = [...new Set(myCases.map(c => c.case_status))];
          
          // Debug: Show detailed breakdown of my cases by status
          const myCasesByStatus = {};
          myCases.forEach(c => {
            const status = c.case_status || 'NULL';
            myCasesByStatus[status] = (myCasesByStatus[status] || 0) + 1;
          });
          
          setPersonalStats({
            totalAssigned: myCases.length,
            inProgress: myInProgress,
            closed: myClosed
          });
          
          // Fetch top customers, products, and agents using dedicated statistics endpoints
          try {
            // Fetch top customers
            const topCustomersResponse = await api.get(
              '/statistics/top-customers',
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            
            const topCustomersArray = topCustomersResponse.data
              .slice(0, 5)
              .map(customer => ({ 
                name: customer.customer_name, 
                count: customer.total_cases 
              }));
            
            // Fetch top products
            const topProductsResponse = await api.get(
              '/statistics/product-stats',
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            
            const topProductsArray = topProductsResponse.data
              .slice(0, 5)
              .map(product => ({ 
                name: product.product_name, 
                count: product.total_cases 
              }));
            
            // Fetch top agents
            const topAgentsResponse = await api.get(
              '/statistics/user-stats',
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            
            const topAgentsArray = topAgentsResponse.data
              .slice(0, 5)
              .map(agent => ({ 
                name: agent.agent_name, 
                count: agent.total_cases 
              }));
            
            
            setTopCustomers(topCustomersArray);
            setTopProducts(topProductsArray);
            setTopAgents(topAgentsArray);
          } catch (error) {
            console.error('Error fetching top statistics:', error);
            // Set empty arrays if API calls fail
            setTopCustomers([]);
            setTopProducts([]);
            setTopAgents([]);
          }
        } catch (error) {
          console.error('Error fetching support cases:', error);
        }
        
        // Fetch agents (users)
        let totalAgents = 0;
        try {
          const agentsResponse = await api.get(
            `/agents/company/${companyId}`
          );
          
          
          // The API returns { success: true, agents: [...] }
          if (agentsResponse.data && agentsResponse.data.success && agentsResponse.data.agents) {
            totalAgents = agentsResponse.data.agents.length;
          } else if (agentsResponse.data && Array.isArray(agentsResponse.data)) {
            totalAgents = agentsResponse.data.length;
          } else {
          }
        } catch (error) {
          console.error('Error fetching agents:', error);
        }
        
        // Fetch customers
        let totalCustomers = 0;
        try {
          const customersResponse = await api.get(
            `/customers/${companyId}`
          );
          
          
          // The API returns { success: true, customers: [...] }
          if (customersResponse.data && customersResponse.data.success && customersResponse.data.customers) {
            totalCustomers = customersResponse.data.customers.length;
          } else if (customersResponse.data && Array.isArray(customersResponse.data)) {
            totalCustomers = customersResponse.data.length;
          } else {
          }
        } catch (error) {
          console.error('Error fetching customers:', error);
        }
        
        // Fetch contracts
        let totalContracts = 0;
        try {
          const contractsResponse = await api.get(
            `/contracts/company/${companyId}`
          );
          
          
          // The API returns { success: true, contracts: [...] }
          if (contractsResponse.data && contractsResponse.data.success && contractsResponse.data.contracts) {
            totalContracts = contractsResponse.data.contracts.length;
          } else if (contractsResponse.data && Array.isArray(contractsResponse.data)) {
            totalContracts = contractsResponse.data.length;
          } else {
          }
        } catch (error) {
          console.error('Error fetching contracts:', error);
        }
        
                 // Update stats
         const newStats = {
           assignedCases,
           totalCases: allCases.length,
           openCases,
           inProgressCases,
           closedCases,
           totalAgents,
           totalCustomers,
           totalContracts
         };
        
        setStats(newStats);
        
      } catch (error) {
        console.error(t('messages.dashboardDataLoadError'), error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);



  // Filtreleme işlevleri
  const handlePersonalStatsClick = (filterType) => {
    let filter = '';
    let message = '';
    let filterType_ = '';
    
    switch(filterType) {
      case 'total':
        filter = 'ASSIGNED';
        filterType_ = 'owner';
        message = t('dashboard.filterMessages.allTicketsAssignedToYou');
        break;
      case 'inProgress':
        filter = 'IN PROGRESS';
        filterType_ = 'owner_status';
        message = t('dashboard.filterMessages.yourInProgressAssignedTickets');
        break;
      case 'closed':
        filter = 'CLOSED';
        filterType_ = 'owner_status';
        message = t('dashboard.filterMessages.yourClosedAssignedTickets');
        break;
      default:
        filter = '';
        filterType_ = '';
        message = '';
    }
    
    navigate('/support-cases', { 
      state: { 
        filter: filter, 
        filterType: filterType_,
        showToast: true,
        toastMessage: message
      } 
    });
  };

  // Sistem geneli filtreleme işlevleri
  const handleSystemStatsClick = (filterType) => {
    let filter = '';
    let message = '';
    let filterType_ = '';
    
    switch(filterType) {
      case 'open':
        filter = 'OPEN';
        filterType_ = 'status';
        message = t('dashboard.filterMessages.allOpenTicketsInSystem');
        break;
      case 'inProgress':
        filter = 'IN PROGRESS';
        filterType_ = 'status';
        message = t('dashboard.filterMessages.allInProgressTicketsInSystem');
        break;
      case 'closed':
        filter = 'CLOSED';
        filterType_ = 'status';
        message = t('dashboard.filterMessages.allClosedTicketsInSystem');
        break;
      case 'total':
        filter = 'ALL';
        filterType_ = 'all';
        message = t('dashboard.filterMessages.allTicketsInSystemDisplayed');
        break;
      default:
        filter = '';
        filterType_ = '';
        message = '';
    }
    
    navigate('/support-cases', { 
      state: { 
        filter: filter, 
        filterType: filterType_,
        showToast: true,
        toastMessage: message
      } 
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    
    
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl text-gray-800 dark:text-white">{t('navigation.dashboard')}</h1>
        

      </div>
     

      
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between  p-4 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <MdPerson className="text-gray-800 dark:text-white mr-2" size={20} />
              <h2 className="text-lg  text-gray-800 dark:text-white">{t('dashboard.myPersonalStatistics')}</h2>
            </div>
          </div>
          <div className="p-4">
            <div className="flex flex-col mb-6">
              <div className="text-center mb-4">
                
              </div>
              <div className="flex justify-around">
                <div className="flex flex-col items-center cursor-pointer" onClick={() => handlePersonalStatsClick('total')}>
                  <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4 hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200">
                    <MdAssignment className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                    <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{personalStats.totalAssigned}</span>
                  </div>
                  <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.total')}</span>
                </div>
                
                <div className="flex flex-col items-center cursor-pointer" onClick={() => handlePersonalStatsClick('inProgress')}>
                  <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4 hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200">
                    <TbProgressCheck className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                    <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{personalStats.inProgress}</span>
                  </div>
                  <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.inProgress')}</span>
                </div>
                
                <div className="flex flex-col items-center cursor-pointer" onClick={() => handlePersonalStatsClick('closed')}>
                  <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4 hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200">
                    <MdCheckCircle className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                    <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{personalStats.closed}</span>
                  </div>
                  <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.closed')}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4">

            </div>
          </div>
        </div>        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between  p-4 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <FaChartPie className="text-gray-800 dark:text-white mr-2" size={20} />
              <h2 className="text-lg  text-gray-800 dark:text-white">{t('dashboard.supportCaseDistribution')}</h2>
            </div>
            <div className="flex items-center space-x-2">
              {/* Empty div to match the structure */}
            </div>
          </div>
          <div className="p-4">
            <div className="flex flex-col mb-6">
              <div className="text-center mb-4">
                {/* Empty div to match the structure */}
              </div>
                             <div className="flex justify-around">
                                  <div className="flex flex-col items-center cursor-pointer" onClick={() => handleSystemStatsClick('open')}>
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4 hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200">
                      <MdTask className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                      <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{stats.openCases}</span>
                    </div>
                    <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.open')}</span>
                  </div>
                
                                  <div className="flex flex-col items-center cursor-pointer" onClick={() => handleSystemStatsClick('inProgress')}>
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4 hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200">
                      <TbProgressCheck className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                      <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{stats.inProgressCases}</span>
                    </div>
                    <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.inProgress')}</span>
                  </div>
                  
                  <div className="flex flex-col items-center cursor-pointer" onClick={() => handleSystemStatsClick('closed')}>
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4 hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200">
                      <MdCheckCircle className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                      <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{stats.closedCases}</span>
                    </div>
                    <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.closed')}</span>
                  </div>
                
                 <div className="flex flex-col items-center cursor-pointer" onClick={() => handleSystemStatsClick('total')}>
                   <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4 hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200">
                     <BiTask className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                     <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{stats.totalCases}</span>
                   </div>
                   <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.total')}</span>
                 </div>
               </div>
            </div>
            
            <div className="mt-4">
              {/* Empty div to match the structure */}
            </div>
          </div>
        </div>
        

        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between  p-4 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <FaChartLine className="text-gray-800 dark:text-white mr-2" size={20} />
              <h2 className="text-lg text-gray-800 dark:text-white">{t('dashboard.systemStatistics')}</h2>
            </div>
            <div className="flex items-center space-x-2">
              {/* Empty div to match the structure */}
            </div>
          </div>
          <div className="p-4">
            <div className="flex flex-col mb-6">
              <div className="text-center mb-4">
                {/* Empty div to match the structure */}
              </div>
              <div className="flex justify-around">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4">
                    <MdSupportAgent className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                    <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{stats.totalAgents}</span>
                  </div>
                  <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.totalAgents')}</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4">
                    <FaUsers className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                    <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{stats.totalCustomers}</span>
                  </div>
                  <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.totalCustomers')}</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 dark:bg-gray-600 rounded-lg shadow-md flex flex-col items-center justify-center p-2 sm:p-4">
                    <FaFileContract className="text-gray-800 dark:text-white mb-1 sm:mb-2" size={24} />
                    <span className="text-sm sm:text-xl font-bold text-gray-800 dark:text-white">{stats.totalContracts}</span>
                  </div>
                  <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.totalContracts')}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              {/* Empty div to match the structure */}
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between  p-4 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <MdBusiness className="text-gray-800 dark:text-white mr-2" size={20} />
              <h2 className="text-lg  text-gray-800 dark:text-white">{t('dashboard.topCustomersBySupportVolume')}</h2>
            </div>
            <div className="flex items-center space-x-2">
              {/* Empty div to match the structure */}
            </div>
          </div>
          <div className="p-6">
            {topCustomers.length > 0 ? (
              <div className="space-y-3">
                {topCustomers.map((customer, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                      index === 0 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      index === 1 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      index === 2 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">{customer.name}</span>
                        <span className="text-xs font-bold bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white px-2 py-1 rounded-full">
                          {customer.count} {t('dashboard.calls')}
                        </span>
                      </div>
                  
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                {t('dashboard.noCustomerDataFound')}
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between  p-4 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <MdCategory className="text-gray-800 dark:text-white mr-2" size={20} />
              <h2 className="text-lg  text-gray-800 dark:text-white">{t('dashboard.topProductsBySupportVolume')}</h2>
            </div>
            <div className="flex items-center space-x-2">
              {/* Empty div to match the structure */}
            </div>
          </div>
          <div className="p-6">
            {topProducts.length > 0 ? (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                      index === 0 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      index === 1 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      index === 2 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">{product.name}</span>
                        <span className="text-xs font-bold bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white px-2 py-1 rounded-full">
                          {product.count} {t('dashboard.calls')}
                        </span>
                      </div>
             
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                {t('dashboard.noProductDataFound')}
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between  p-4 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <FaUserTie className="text-gray-800 dark:text-white mr-2" size={20} />
              <h2 className="text-lg  text-gray-800 dark:text-white">{t('dashboard.mostActiveAgent')}</h2>
            </div>
            <div className="flex items-center space-x-2">
              {/* Empty div to match the structure */}
            </div>
          </div>
          <div className="p-6">
            {topAgents.length > 0 ? (
              <div className="space-y-3">
                {topAgents.map((agent, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                      index === 0 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      index === 1 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      index === 2 ? 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white' : 
                      'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">{agent.name}</span>
                        <span className="text-xs font-bold bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white px-2 py-1 rounded-full">
                          {agent.count} {t('dashboard.calls')}
                        </span>
                      </div>
                   
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                {t('dashboard.noAgentDataFound')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    
  );
};

export default DashboardPage; 